import { safeSessionStorageGet, safeSessionStorageRemove, safeSessionStorageSet } from "@/lib/safe-browser";

const POST_LOGIN_REDIRECT_KEY = "routify:post-login-redirect";
const POST_LOGIN_REDIRECT_TTL_MS = 20 * 60_000;

function normalizeAppPath(candidate?: string | null): string | null {
  if (!candidate || typeof window === "undefined") return null;
  try {
    const url = new URL(candidate, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    const path = `${url.pathname}${url.search}${url.hash}`;
    if (!path.startsWith("/") || path.startsWith("//")) return null;
    if (url.pathname === "/login" || url.pathname === "/auth" || url.pathname === "/auth/callback") return null;
    if (url.pathname.startsWith("/~oauth")) return null;
    return path;
  } catch {
    return null;
  }
}

function currentAppPath(): string | null {
  if (typeof window === "undefined") return null;
  return normalizeAppPath(`${window.location.pathname}${window.location.search}${window.location.hash}`);
}

export function storePostLoginRedirect(candidate?: string | null) {
  if (typeof window === "undefined") return;
  const path = normalizeAppPath(candidate) ?? currentAppPath();
  if (!path || path === "/") return;
  try {
    safeSessionStorageSet(POST_LOGIN_REDIRECT_KEY, JSON.stringify({ path, savedAt: Date.now() }));
  } catch {
    // opcional: se o browser bloquear storage, simplesmente não preservamos o destino
  }
}

function readStoredPostLoginRedirect(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = safeSessionStorageGet(POST_LOGIN_REDIRECT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { path?: string; savedAt?: number } | string;
    if (typeof parsed === "string") return normalizeAppPath(parsed);
    if (!parsed?.path || !parsed?.savedAt) return null;
    if (Date.now() - parsed.savedAt > POST_LOGIN_REDIRECT_TTL_MS) return null;
    return normalizeAppPath(parsed.path);
  } catch {
    return null;
  }
}

function readRedirectSearchParam(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    return normalizeAppPath(params.get("redirect") ?? params.get("next"));
  } catch {
    return null;
  }
}

export function clearPostLoginRedirect() {
  try {
    safeSessionStorageRemove(POST_LOGIN_REDIRECT_KEY);
  } catch {
    // opcional
  }
}

export function consumePostLoginRedirect(fallback = "/") {
  const target = readRedirectSearchParam() ?? readStoredPostLoginRedirect() ?? fallback;
  clearPostLoginRedirect();
  return normalizeAppPath(target) ?? fallback;
}

/**
 * TanStack Router lança "Invariant failed" quando `to` inclui query string ou
 * hash (ex.: `/pro?fbclid=...` vindo do in-app browser do Instagram).
 * Este helper separa caminho, search e hash — e remove identificadores de
 * tracking — para que a navegação pós-login nunca gere erro não tratado.
 */
export function splitNavigationTarget(path: string): {
  to: string;
  search: Record<string, string>;
  hash?: string;
} {
  const base = typeof window === "undefined" ? "https://routify-tour.com" : window.location.origin;
  try {
    const url = new URL(path, base);
    const search: Record<string, string> = {};
    for (const [key, value] of url.searchParams) {
      const lower = key.toLowerCase();
      if (TRACKING_PARAMS.includes(lower)) continue;
      search[key] = value;
    }
    const to = url.pathname || "/";
    const hash = url.hash ? url.hash.replace(/^#/, "") : undefined;
    return { to, search, hash };
  } catch {
    return { to: "/", search: {} };
  }
}

const TRACKING_PARAMS = ["fbclid", "gclid", "igshid", "igsh", "msclkid", "ttclid", "twclid", "mc_eid", "mc_cid"];


export function replaceWithPostLoginRedirect(fallback = "/") {
  if (typeof window === "undefined") return;
  const target = consumePostLoginRedirect(fallback);
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  window.location.replace(target === current ? fallback : target);
}